import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SystemDetails } from '@twaice-fe/shared/models';
import { BehaviorSubject } from 'rxjs';
import { SystemOverviewPopoverComponent } from './system-overview-popover/system-overview-popover.component';

interface SystemMarker {
  id: string;
  name: string;
  coordinates: [number, number]; // [longitude, latitude]
}

@Component({
  selector: 'twaice-fe-system-overview-map',
  templateUrl: './system-overview-map.component.html',
  styleUrls: ['./system-overview-map.component.scss'],
  standalone: true,
  imports: [CommonModule, SystemOverviewPopoverComponent],
})
export class SystemOverviewMapComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('container', { static: false }) container!: ElementRef<HTMLDivElement>;
  @Input() systems: SystemDetails[] = [];
  /** When set, applied after fitting markers so the user’s last zoom level is restored. */
  @Input() configurationZoom: number | null | undefined;
  /**
   * When false, markers do not show the popover or react to hover; marker click still
   * runs zoom-to-marker, and a subsequent map click zooms back to all markers. Pan/drag
   * works when dragging from outside marker hit targets.
   */
  @Input() interactiveMarkers = true;
  @Output() zoomChange = new EventEmitter<number>();

  selectedSystem$ = new BehaviorSubject<SystemDetails | null>(null);
  showPopover = false;
  showScrollOverlay = false;
  private hoveredSystem: SystemDetails | null = null;
  private isHovering = false;
  private clickedSystem: SystemDetails | null = null;

  maplibregl;

  private map: maplibregl.Map | null = null;
  private markers: maplibregl.Marker[] = [];
  private systemMarkerMap = new Map<maplibregl.Marker, SystemDetails>();
  private allMarkersBounds: maplibregl.LngLatBounds | null = null;
  private programmaticCameraChange = false;
  /** Emits zoomChange after some user-initiated programmatic camera changes (e.g. marker click zoom). */
  private emitZoomAfterProgrammaticZoomEnd = false;
  private scrollOverlayTimeout: ReturnType<typeof setTimeout> | null = null;
  private wheelListener: ((e: WheelEvent) => void) | null = null;

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    // @ts-ignore
    import('maplibre-gl').then((module) => {
      this.maplibregl = module.default;
      this.initializeMap();
      this.setupScrollIntercept();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const systemsChanged = changes['systems'] && !changes['systems'].firstChange;
    const interactiveMarkersChanged = changes['interactiveMarkers'] && !changes['interactiveMarkers'].firstChange;
    const configurationZoomChanged = changes['configurationZoom'] && !changes['configurationZoom'].firstChange;

    if ((systemsChanged || interactiveMarkersChanged) && this.map) {
      this.updateMarkers();
    }

    if (configurationZoomChanged) {
      this.applyConfigurationZoom();
    }
  }

  ngOnDestroy(): void {
    this.clearMarkers();
    if (this.wheelListener && this.mapContainer?.nativeElement) {
      this.mapContainer.nativeElement.removeEventListener('wheel', this.wheelListener);
    }
    if (this.scrollOverlayTimeout) {
      clearTimeout(this.scrollOverlayTimeout);
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  onPopoverClose(): void {
    this.showPopover = false;
    this.selectedSystem$.next(null);
    this.clickedSystem = null;
  }

  onMapClick(): void {
    // Close popover when clicking on the map
    // Only zoom if popover was opened by click (not hover)
    if (this.clickedSystem) {
      this.onPopoverClose();
      this.zoomToAllMarkers();
    } else {
      // If opened by hover, just close popover without zooming
      this.onPopoverClose();
    }
  }

  private async initializeMap(): Promise<void> {
    if (!this.mapContainer?.nativeElement) {
      return;
    }

    // @ts-ignore
    this.map = new this.maplibregl.Map({
      container: this.mapContainer.nativeElement,
      style: '/assets/maplibre/style.json',
      center: [0, 0],
      zoom: 2,
      attributionControl: false,
      scrollZoom: false,
    });

    // @ts-ignore
    this.map.addControl(new this.maplibregl.NavigationControl(), 'top-right');
    // @ts-ignore
    this.map.addControl(new this.maplibregl.FullscreenControl({ container: this.container.nativeElement }));

    // Close popover when clicking on the map
    this.map.on('click', () => {
      this.onMapClick();
    });

    this.map.on('load', () => {
      this.updateMarkers();
      this.applyConfigurationZoom();
    });

    this.map.on('zoomend', () => this.emitUserZoomIfNeeded());
  }

  private applyConfigurationZoom(): void {
    if (!this.map) {
      return;
    }
    if (this.configurationZoom == null) {
      return;
    }

    this.programmaticCameraChange = true;
    this.afterZoomEnd(() => {
      this.programmaticCameraChange = false;
    });
    this.map.setZoom(this.configurationZoom);
  }

  private emitUserZoomIfNeeded(): void {
    if (this.programmaticCameraChange || !this.map) {
      return;
    }
    this.zoomChange.emit(Number(this.map.getZoom().toFixed(2)));
  }

  private afterMoveEnd(done: () => void): void {
    if (!this.map) {
      return;
    }
    this.map.once('moveend', done);
  }

  private afterZoomEnd(done: () => void): void {
    if (!this.map) {
      return;
    }
    this.map.once('zoomend', done);
  }

  private updateMarkers(): void {
    if (!this.map) {
      return;
    }

    this.clearMarkers();

    const systemMarkers = this.getSystemMarkers();

    if (systemMarkers.length === 0) {
      return;
    }

    // Calculate bounds to fit all markers
    // @ts-ignore
    const bounds = new this.maplibregl.LngLatBounds();

    systemMarkers.forEach((systemMarker) => {
      const system = this.systems.find((s) => s.id === systemMarker.id);
      if (!system) {
        return;
      }

      // @ts-ignore
      const marker = new this.maplibregl.Marker().setLngLat(systemMarker.coordinates).addTo(this.map as maplibregl.Map);

      const markerElement = marker.getElement();

      if (this.interactiveMarkers) {
        markerElement.style.cursor = 'pointer';
        markerElement.title = systemMarker.name;

        // Click handler - zooms and shows popover
        markerElement.addEventListener('click', (e) => {
          e.stopPropagation();
          this.onMarkerClick(system);
        });

        // Hover handlers - shows popover without zooming
        markerElement.addEventListener('mouseenter', (e) => {
          e.stopPropagation();
          this.onMarkerHover(system);
        });

        markerElement.addEventListener('mouseleave', (e) => {
          e.stopPropagation();
          this.onMarkerLeave(system);
        });
      } else {
        markerElement.style.cursor = 'pointer';
        markerElement.title = systemMarker.name;
        markerElement.addEventListener('click', (e) => {
          e.stopPropagation();
          this.zoomToMarker(system);
        });
      }

      this.markers.push(marker);
      this.systemMarkerMap.set(marker, system);
      bounds.extend(systemMarker.coordinates);
    });

    // Store bounds for zooming back out
    this.allMarkersBounds = bounds;

    this.programmaticCameraChange = true;

    // Fit map to show all markers
    if (systemMarkers.length > 1) {
      this.map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 10,
      });
      this.afterMoveEnd(() => {
        if (this.configurationZoom != null && this.map) {
          this.afterZoomEnd(() => {
            this.programmaticCameraChange = false;
          });
          this.map.setZoom(this.configurationZoom);
        } else {
          this.programmaticCameraChange = false;
        }
      });
    } else if (systemMarkers.length === 1) {
      this.map.setCenter(systemMarkers[0].coordinates);
      this.afterZoomEnd(() => {
        this.programmaticCameraChange = false;
      });
      this.map.setZoom(this.configurationZoom ?? 6);
    }
  }

  private getSystemMarkers(): SystemMarker[] {
    return this.systems
      .map((system) => {
        const coordinates = this.getCoordinatesForSystem(system);
        if (!coordinates) {
          return null;
        }
        return {
          id: system.id,
          name: system.systemName,
          coordinates,
        };
      })
      .filter((marker): marker is SystemMarker => marker !== null)
      .sort((a, b) => b.coordinates[1] - a.coordinates[1]); // Sort by latitude descending
  }

  private getCoordinatesForSystem(system: SystemDetails): [number, number] | null {
    const { locationLongitude, locationLatitude } = system.bessMetadata;
    if (!locationLongitude || !locationLatitude) {
      return null;
    }

    return [locationLongitude, locationLatitude];
  }

  private clearMarkers(): void {
    this.markers.forEach((marker) => marker.remove());
    this.markers = [];
    this.systemMarkerMap.clear();
  }

  private onMarkerClick(system: SystemDetails): void {
    this.isHovering = false;
    this.clickedSystem = system;
    this.selectedSystem$.next(system);
    this.showPopover = true;
    this.zoomToMarker(system);
  }

  private onMarkerHover(system: SystemDetails): void {
    this.isHovering = true;
    this.hoveredSystem = system;
    // Show popover on hover (only if not already clicked on this system)
    if (this.clickedSystem?.id !== system.id) {
      this.selectedSystem$.next(system);
      this.showPopover = true;
    }
  }

  private onMarkerLeave(system: SystemDetails): void {
    // Only close popover if it was opened by hover (not by click)
    if (this.clickedSystem?.id === system.id) {
      // Don't close if this was a clicked system
      return;
    }

    // Temporarily clear hover state - will be set again if moving to another marker
    const wasHoveringThisSystem = this.hoveredSystem?.id === system.id;
    if (wasHoveringThisSystem) {
      this.hoveredSystem = null;
      this.isHovering = false;
    }

    // Use a small delay to check if we're moving to another marker
    // This allows smooth transition between markers
    setTimeout(() => {
      // If we're no longer hovering any marker and popover was opened by hover, close it
      if (!this.isHovering && !this.hoveredSystem && wasHoveringThisSystem && this.clickedSystem?.id !== system.id) {
        this.onPopoverClose();
      }
    }, 150);
  }

  private zoomToMarker(system: SystemDetails): void {
    if (!this.map) {
      return;
    }

    const coordinates = this.getCoordinatesForSystem(system);
    if (!coordinates) {
      return;
    }

    this.programmaticCameraChange = true;
    // Marker click is user intent → persist the resulting zoom.
    this.emitZoomAfterProgrammaticZoomEnd = true;
    this.afterZoomEnd(() => {
      this.programmaticCameraChange = false;
      if (this.emitZoomAfterProgrammaticZoomEnd) {
        this.emitZoomAfterProgrammaticZoomEnd = false;
        this.emitUserZoomIfNeeded();
      }
    });
    // Zoom into the marker location
    this.map.flyTo({
      center: coordinates,
      zoom: 13,
      duration: 2000,
    });
  }

  private zoomToAllMarkers(): void {
    if (!this.map || !this.allMarkersBounds) {
      return;
    }

    this.programmaticCameraChange = true;
    this.afterZoomEnd(() => {
      this.programmaticCameraChange = false;
    });
    // Zoom back out to show all markers (fitBounds animates by default)
    this.map.fitBounds(this.allMarkersBounds, {
      padding: 50,
      maxZoom: 10,
      duration: 1000,
    });
  }

  private setupScrollIntercept(): void {
    if (!this.mapContainer?.nativeElement || !this.map) return;

    this.wheelListener = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const zoomDelta = -e.deltaY * 0.01;
        this.map?.setZoom((this.map.getZoom() ?? 0) + zoomDelta);
      } else {
        this.showCtrlScrollOverlay();
      }
    };

    this.ngZone.runOutsideAngular(() => {
      this.mapContainer.nativeElement.addEventListener('wheel', this.wheelListener!, { passive: false });
    });
  }

  private showCtrlScrollOverlay(): void {
    if (this.scrollOverlayTimeout) {
      clearTimeout(this.scrollOverlayTimeout);
    }

    this.ngZone.run(() => {
      this.showScrollOverlay = true;
      this.cdr.markForCheck();
    });

    this.scrollOverlayTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        this.showScrollOverlay = false;
        this.cdr.markForCheck();
      });
    }, 1500);
  }
}
